import { FilesetResolver, PoseLandmarker, type NormalizedLandmark } from "@mediapipe/tasks-vision";
import type { PoseDetector } from "@tensorflow-models/pose-detection";
import {
  CHOREOGRAPHY_VERSION,
  DEFAULT_SCORING_CONFIG,
  PoseLandmarkIndex,
  POSE_LANDMARK_COUNT,
  extractPoseFeatures,
  toAspectCorrectedLandmarks,
  type Choreography,
  type ChoreographyFrame,
  type Landmark,
} from "@dance-game/core";
import { seekTo, waitForMetadata } from "./seekVideo.js";

/**
 * Extracts a reference pose timeline from a local video.
 *
 * Deliberately separate from `MediaPipePoseProvider`: that one is tuned for a
 * live camera, where it skips unchanged frames and smooths across time. Both
 * behaviours are wrong here. Extraction visits every target timestamp exactly
 * once and stores raw landmarks, so the file is reproducible and the scorer
 * decides later how to filter it.
 */

export type ExtractionOptions = {
  video: HTMLVideoElement;
  id: string;
  title: string;
  videoPath: string;
  mirrored: boolean;
  /** Samples per second along the timeline. */
  sampleRateHz: number;
  engine?: ExtractionEngine;
  /** Seconds into the video where the choreography starts. */
  trimStartSeconds: number;
  /** Seconds into the video where it ends; defaults to the video duration. */
  trimEndSeconds?: number;
  wasmBasePath?: string;
  modelAssetPath?: string;
  onProgress?: (progress: ExtractionProgress) => void;
  signal?: AbortSignal;
};

export type ExtractionEngine = "mediapipe" | "movenet";

export type ExtractionProgress = {
  completedSamples: number;
  totalSamples: number;
  /** Samples where no dancer was detected. */
  missedSamples: number;
  currentTMs: number;
};

const DEFAULT_WASM_BASE_PATH = "/mediapipe/wasm";
/**
 * The heavier model. Extraction is offline and one-off, so quality matters
 * more than speed here, unlike on the phone.
 */
const DEFAULT_MODEL_PATH =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_full/float16/latest/pose_landmarker_full.task";

export class ExtractionAbortedError extends Error {
  constructor() {
    super("Extraction was cancelled.");
    this.name = "ExtractionAbortedError";
  }
}

/**
 * Stored coordinate precision.
 *
 * Full float precision triples the file size for digits that sit far below the
 * pose model's own noise. 4 decimals matches what the wire protocol sends.
 */
const STORED_DECIMALS = 4;

function round(value: number): number {
  const factor = 10 ** STORED_DECIMALS;
  return Math.round(value * factor) / factor;
}

function toLandmark(source: NormalizedLandmark): Landmark {
  return {
    x: source.x,
    y: source.y,
    z: source.z,
    visibility: typeof source.visibility === "number" ? source.visibility : 1,
  };
}

/** Rounds for storage. Must run after aspect correction, not before it. */
function roundLandmarks(landmarks: readonly Landmark[]): Landmark[] {
  return landmarks.map((landmark) => ({
    x: round(landmark.x),
    y: round(landmark.y),
    z: round(landmark.z),
    visibility: round(landmark.visibility),
  }));
}

const MOVENET_LANDMARK_INDEX: Readonly<Record<string, number>> = {
  nose: PoseLandmarkIndex.nose,
  left_eye: PoseLandmarkIndex.leftEye,
  right_eye: PoseLandmarkIndex.rightEye,
  left_ear: PoseLandmarkIndex.leftEar,
  right_ear: PoseLandmarkIndex.rightEar,
  left_shoulder: PoseLandmarkIndex.leftShoulder,
  right_shoulder: PoseLandmarkIndex.rightShoulder,
  left_elbow: PoseLandmarkIndex.leftElbow,
  right_elbow: PoseLandmarkIndex.rightElbow,
  left_wrist: PoseLandmarkIndex.leftWrist,
  right_wrist: PoseLandmarkIndex.rightWrist,
  left_hip: PoseLandmarkIndex.leftHip,
  right_hip: PoseLandmarkIndex.rightHip,
  left_knee: PoseLandmarkIndex.leftKnee,
  right_knee: PoseLandmarkIndex.rightKnee,
  left_ankle: PoseLandmarkIndex.leftAnkle,
  right_ankle: PoseLandmarkIndex.rightAnkle,
};

function mapMoveNetPose(
  keypoints: readonly { name?: string; x: number; y: number; score?: number }[],
  imageWidth: number,
  imageHeight: number,
): Landmark[] {
  const landmarks: Landmark[] = Array.from({ length: POSE_LANDMARK_COUNT }, () => ({
    x: 0,
    y: 0,
    z: 0,
    visibility: 0,
  }));
  for (const keypoint of keypoints) {
    const index = keypoint.name ? MOVENET_LANDMARK_INDEX[keypoint.name] : undefined;
    if (index === undefined) {
      continue;
    }
    landmarks[index] = {
      x: keypoint.x / imageWidth,
      y: keypoint.y / imageHeight,
      z: 0,
      visibility: keypoint.score ?? 0,
    };
  }
  return toAspectCorrectedLandmarks(landmarks, imageWidth, imageHeight);
}

async function createMoveNetDetector(): Promise<PoseDetector> {
  const [tf, , poseDetection] = await Promise.all([
    import("@tensorflow/tfjs-core"),
    import("@tensorflow/tfjs-backend-webgl"),
    import("@tensorflow-models/pose-detection"),
  ]);
  await tf.setBackend("webgl");
  await tf.ready();
  return poseDetection.createDetector(poseDetection.SupportedModels.MoveNet, {
    modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
    enableSmoothing: false,
    minPoseScore: 0.1,
  });
}

function hasScoreableGroup(landmarks: readonly Landmark[]): boolean {
  const features = extractPoseFeatures(landmarks);
  return (
    features !== undefined &&
    Object.values(features.confidence).some(
      (confidence) => confidence >= DEFAULT_SCORING_CONFIG.minVisibility,
    )
  );
}

export async function extractChoreography(options: ExtractionOptions): Promise<Choreography> {
  const {
    video,
    id,
    title,
    videoPath,
    mirrored,
    sampleRateHz,
    engine = "mediapipe",
    trimStartSeconds,
    onProgress,
    signal,
  } = options;

  if (sampleRateHz <= 0) {
    throw new Error(`sampleRateHz must be positive, received ${sampleRateHz}`);
  }

  await waitForMetadata(video);

  const duration = video.duration;
  if (!Number.isFinite(duration) || duration <= 0) {
    throw new Error("The video has no usable duration.");
  }

  const endSeconds = Math.min(options.trimEndSeconds ?? duration, duration);
  if (endSeconds <= trimStartSeconds) {
    throw new Error("The trim end must come after the trim start.");
  }

  const imageWidth = video.videoWidth;
  const imageHeight = video.videoHeight;
  if (imageWidth === 0 || imageHeight === 0) {
    throw new Error("The video has no usable dimensions.");
  }

  let landmarker: PoseLandmarker | undefined;
  let moveNetDetector: PoseDetector | undefined;
  if (engine === "movenet") {
    moveNetDetector = await createMoveNetDetector();
  } else {
    const fileset = await FilesetResolver.forVisionTasks(
      options.wasmBasePath ?? DEFAULT_WASM_BASE_PATH,
    );
    landmarker = await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: options.modelAssetPath ?? DEFAULT_MODEL_PATH,
        delegate: "GPU",
      },
      runningMode: "VIDEO",
      numPoses: 1,
      outputSegmentationMasks: false,
    });
  }

  try {
    const intervalMs = 1000 / sampleRateHz;
    const spanMs = (endSeconds - trimStartSeconds) * 1000;
    const totalSamples = Math.floor(spanMs / intervalMs) + 1;

    const frames: ChoreographyFrame[] = [];
    let missedSamples = 0;

    for (let index = 0; index < totalSamples; index++) {
      if (signal?.aborted) {
        throw new ExtractionAbortedError();
      }

      // tMs is relative to the trimmed start, which is what the game seeks to.
      const tMs = Math.round(index * intervalMs);
      const videoTimeSeconds = trimStartSeconds + tMs / 1000;

      await seekTo(video, videoTimeSeconds);

      // MediaPipe requires strictly increasing timestamps in VIDEO mode. The
      // sample index is used rather than a wall clock so a re-run of the same
      // video feeds the model the identical sequence.
      const result = landmarker?.detectForVideo(video, tMs);
      const moveNetPose = moveNetDetector
        ? (await moveNetDetector.estimatePoses(video, { maxPoses: 1 }, tMs))[0]
        : undefined;
      const rawLandmarks = result?.landmarks[0];
      const rawWorldLandmarks = result?.worldLandmarks[0];
      const landmarks = moveNetPose
        ? mapMoveNetPose(moveNetPose.keypoints, imageWidth, imageHeight)
        : rawLandmarks
          ? toAspectCorrectedLandmarks(rawLandmarks.map(toLandmark), imageWidth, imageHeight)
          : undefined;

      if (!landmarks || !hasScoreableGroup(landmarks)) {
        // Record the gap rather than dropping the sample: the timeline must
        // stay evenly spaced, and the scorer needs to know the reference is
        // unusable here instead of interpolating across the hole.
        missedSamples += 1;
        frames.push({ tMs, landmarks: [] });
      } else {
        frames.push({
          tMs,
          landmarks: roundLandmarks(landmarks),
          ...(rawWorldLandmarks
            ? { worldLandmarks: roundLandmarks(rawWorldLandmarks.map(toLandmark)) }
            : {}),
        });
      }

      onProgress?.({
        completedSamples: index + 1,
        totalSamples,
        missedSamples,
        currentTMs: tMs,
      });
    }

    return {
      version: CHOREOGRAPHY_VERSION,
      id,
      title,
      videoPath,
      mirrored,
      sampleRateHz,
      imageWidth,
      imageHeight,
      frames,
    };
  } finally {
    landmarker?.close();
    moveNetDetector?.dispose();
  }
}
